import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { FiWrapperTableAdditionalColumns } from '@fi-shared/fi-wrapper-table/fi-wrapper-table-additional-columns';
import { IChecklist2ReportAnswer } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { Checkpoint2MeasureUnitType, Checkpoint2StatusType, CheckpointType } from '@fi-shared/checklist/checklist-enum';
import { FiCheckpoint2HelperService } from '@fi-shared/checklist/fi-checkpoint2-helper.service';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-checklist2-answer-value',
    template: `
        <ng-container matColumnDef="AnswerStatus">
          <mat-header-cell *matHeaderCellDef>
            <fi-wrapper-table-span-cell [value]="'checklist.answers' | fiAppText" />
          </mat-header-cell>
          <mat-cell *matCellDef="let item" class="column-show-mobile">
            <div class="answer-content-wrapper">
              @if (isAnswerPresent(item.AnswerStatus)) {
                <mat-icon
                  class="answer-icon fas"
                  [ngClass]="getAnswerIconClass(item.AnswerStatus)"
                  />
              }
        
              <fi-wrapper-table-span-cell
                [ngClass]="getAnswerColorClass(item)"
                [value]="getAnswerStatusLabel(item)"
                />
                @if (isMeasurementCheckpoint(item)) {
                  <fi-wrapper-table-span-cell
                    class="answer-value-text"
                    [value]="getMeasurementCheckpointValue(item)"
                    />
                }
              </div>
            </mat-cell>
          </ng-container>
        `,
    styleUrls: ['./fi-checklist2-answer-value.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-answer-value' },
    providers: [{ provide: FiWrapperTableAdditionalColumns, useExisting: FiChecklist2AnswerValueComponent }],
    standalone: false,
})
export class FiChecklist2AnswerValueComponent extends FiWrapperTableAdditionalColumns {
    @Input() isNotAnsweredLabelShown: boolean = true;

    constructor(
        private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService,
        private fiCheckpoint2HelperService: FiCheckpoint2HelperService,
    ) {
        super();
    }

    isAnswerPresent(answerStatus: Checkpoint2StatusType): boolean {
        return answerStatus !== Checkpoint2StatusType.NotFilledOut;
    }

    getAnswerIconClass(answerStatus: Checkpoint2StatusType): { [key: string]: boolean } {
        return {
            'fa-circle-exclamation red-color': this.isAnswerNotOk(answerStatus),
            'fa-circle-check green-color': this.isAnswerOk(answerStatus),
        };
    }

    getAnswerColorClass(item: IChecklist2ReportAnswer): { [key: string]: boolean } {
        return {
            'text-red': this.isAnswerNotOk(item.AnswerStatus),
            'text-green': this.isAnswerOk(item.AnswerStatus),
        };
    }

    isMeasurementCheckpoint(answerDetails: IChecklist2ReportAnswer): boolean {
        return isDefined(answerDetails.Value) && answerDetails.CheckpointType === CheckpointType.Measurement;
    }

    private isAnswerOk(answerStatus: Checkpoint2StatusType): boolean {
        return answerStatus === Checkpoint2StatusType.Ok;
    }

    private isAnswerNotOk(answerStatus: Checkpoint2StatusType): boolean {
        return answerStatus === Checkpoint2StatusType.NotOk;
    }

    getAnswerStatusLabel(answerDetails: IChecklist2ReportAnswer): string {
        if (answerDetails.AnswerStatus === Checkpoint2StatusType.NotFilledOut && !this.isNotAnsweredLabelShown) {
            return '';
        }

        return this.fiChecklist2ReportHelperService.getAnswerStatusLabel(answerDetails.AnswerStatus);
    }

    getMeasurementCheckpointValue(answerDetails: IChecklist2ReportAnswer): string {
        return `${answerDetails.Value} ${this.getMeasureUnitsText(answerDetails.CheckpointMeasureUnitType)}`;
    }

    getMeasureUnitsText(measureUnitType: Checkpoint2MeasureUnitType): string {
        return this.fiCheckpoint2HelperService.getCheckpointMeasureUnitShortText(measureUnitType);
    }
}
